"use client"

// services/Recruit/pages/emailTemplates/index.jsx
import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Text,
    Flex,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    Badge,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    Input,
    FormLabel,
    Select,
    Textarea,
    Switch,
    VStack,
    HStack,
    Radio,
    RadioGroup
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon, ViewIcon, EmailIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { toast } from "react-toastify";
import CommonCheckTable from "common/components/checktable";
import CommonDeleteModel from "common/components/commonDeleteModel";
import CustomStatus from "common/components/status/CustomStatus";
import { HasAccess } from "common/utils/accessUtils";
import { accessModuleTitles } from "common/constant";
import { globalStyles } from "common/theme/styles";
import DateFormatter from "common/utils/dateFormater";
import TextEditor from "common/components/textEditor/TextEditor";
import editIcon from "common/assets/img/editIcon.png";
import deleteIcon from "common/assets/img/deleteIcon.png";
import emailTemplateService from "services/Recruit/services/emailTemplate";
import { getApi } from "common/services/api";
import ReactSelect from "react-select";
import {
    getTableFilters,
    saveTableFilters,
    clearTableFilters,
    getInitialFilterState
} from "common/utils/filterUtils";

const emailTemplateCategoryOptions = [
    { value: 'application', label: 'Application' },
    { value: 'performance', label: 'Performance' },
    { value: 'document_request', label: 'Document Request' },
    { value: 'bg_verification', label: 'BG Verification' },
    { value: 'reports', label: 'Reports' },
    { value: 'loi', label: 'LOI' },
    { value: 'birthday_wishes', label: 'Birthday Wishes' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'announcement', label: 'Announcement' },
    { value: 'notification', label: 'Notification' },
    { value: 'reminder', label: 'Reminder' },
    { value: 'welcome', label: 'Welcome' },
    { value: 'custom', label: 'Custom' }
];

const EmailTemplates = () => {
    const title = "Email Templates";
    const TABLE_NAME = 'emailTemplates'; // Define table name constant
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [selectedValues, setSelectedValues] = useState([]);
    const [deleteMany, setDeleteMany] = useState(false);
    const [displaySearchData, setDisplaySearchData] = useState(false);
    const [searchedData, setSearchedData] = useState([]);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [sendOption, setSendOption] = useState('selective'); // 'all' or 'selective'
    const [sendingEmail, setSendingEmail] = useState(false);
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [sortColumn, setSortColumn] = useState(null)
    const [sortOrder, setSortOrder] = useState("")
    const [searchbox, setSearchbox] = useState(() => {
        const filters = getInitialFilterState(TABLE_NAME);
        return filters?.searchbox || "";
    });
    const [finalSearchedData, setFinalSearchedData] = useState([])
    const [metadata, setMetadata] = useState({})

    const colors = globalStyles.colors
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { isOpen: isSendEmailOpen, onOpen: onSendEmailOpen, onClose: onSendEmailClose } = useDisclosure()
    const [selectedTemplateForSend, setSelectedTemplateForSend] = useState(null)

    const [permission] = HasAccess([accessModuleTitles.EmailTemplates])

    // Save filters whenever they change
    useEffect(() => {
        const filters = {
            searchbox: searchbox || "",
        };
        saveTableFilters(TABLE_NAME, filters);
    }, [searchbox]);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'custom',
        subject: '',
        content: '',
        isActive: true,
        isDefault: false
    });

    useEffect(() => {
        fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
        fetchEmployees()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Refetch data when pagination changes
    useEffect(() => {
        fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize])

    const fetchEmployees = async () => {
        try {
            const response = await getApi('api/user/?allData=true')
            console.log('Full API response:', response)

            // Handle different response structures
            const allUsers = response?.data?.data || response?.data || []
            console.log('Fetched users:', allUsers.length, 'Response structure:', {
                hasData: !!response?.data,
                hasDataData: !!response?.data?.data,
                dataType: Array.isArray(response?.data) ? 'array' : typeof response?.data,
                dataDataType: Array.isArray(response?.data?.data) ? 'array' : typeof response?.data?.data
            })

            if (!Array.isArray(allUsers)) {
                console.error('Invalid response format - not an array:', allUsers)
                toast.error('Invalid employee data format')
                setEmployees([])
                return
            }

            console.log('Sample user:', allUsers[0])

            // In this system, email is stored in the 'username' field
            const employeeList = allUsers
                .filter(emp => {
                    if (!emp) return false;

                    // Username contains the email address
                    const email = emp.username || emp.email || '';
                    const hasEmail = email && typeof email === 'string' && email.trim() !== '' && email.includes('@');

                    return hasEmail;
                })
                .map(emp => {
                    const name = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown';
                    // Username contains the email address
                    const email = (emp.username || emp.email || '').trim();
                    // Always show email in brackets
                    const label = `${name} (${email})`;
                    return {
                        value: emp._id,
                        label: label,
                        email: email
                    };
                })

            console.log('Filtered employees with email:', employeeList.length)
            console.log('Employee list sample:', employeeList.slice(0, 3))

            if (employeeList.length === 0) {
                console.warn('No employees with email addresses found. Total users:', allUsers.length)
                toast.warning('No employees with email addresses found')
            }
            setEmployees(employeeList)
        } catch (error) {
            console.error('Failed to fetch employees:', error)
            console.error('Error details:', error.response || error.message)
            toast.error('Failed to load employees: ' + (error.message || 'Unknown error'))
            setEmployees([]) // Set empty array on error
        }
    }

    const fetchData = async (
        page = currentPage,
        limit = pageSize,
        sortKey = sortColumn,
        order = sortOrder,
        searchTerm = "",
    ) => {
        setIsLoading(true)
        try {
            const params = { page, limit }
            if (sortKey) {
                params.sort = order === "desc" ? `-${sortKey}` : sortKey
            }
            if (searchTerm) {
                params.search = searchTerm
            }

            const response = await emailTemplateService.getTemplates(params)
            // Service returns response.data, so response = { metadata, data }
            const list = response?.data ?? []
            const meta = response?.metadata ?? {
                page,
                limit,
                totalRecords: Array.isArray(list) ? list.length : 0,
                totalPages: Math.ceil((Array.isArray(list) ? list.length : 0) / limit),
            }

            const baseIndex = (meta.page - 1) * meta.limit
            const formattedData = (list || []).map((item, index) => ({
                _id: item._id,
                id: item._id,
                srNo: baseIndex + index + 1,
                name: item.name,
                description: item.description || "-",
                category: item.category,
                isActive: item.isActive,
                isDefault: item.isDefault,
                usageCount: item.usageCount,
                lastUsed: item.lastUsed ? DateFormatter({ date: item.lastUsed }) : "Never",
                createdBy: `${item.createBy?.firstName || ""} ${item.createBy?.lastName || ""}`.trim() || "-",
                createdAt: DateFormatter({ date: item.createdAt }),
                rawData: item,
            }))

            setData(formattedData)
            setMetadata(meta)
        } catch (error) {
            toast.error("Failed to fetch templates")
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleOpenModal = (template = null) => {
        if (template) {
            setEditingTemplate(template);
            setFormData({
                name: template.name,
                description: template.description || '',
                category: template.category,
                subject: template.subject,
                content: template.content,
                isActive: template.isActive,
                isDefault: template.isDefault
            });
        } else {
            setEditingTemplate(null);
            setFormData({
                name: '',
                description: '',
                category: 'custom',
                subject: '',
                content: '',
                isActive: true,
                isDefault: false
            });
        }
        onOpen();
    };

    const handleCloseModal = () => {
        setEditingTemplate(null);
        setFormData({
            name: '',
            description: '',
            category: 'custom',
            subject: '',
            content: '',
            isActive: true,
            isDefault: false
        });
        onClose();
    };

    const handleSearchChange = (term) => {
        const trimmed = term?.trim() ?? ""
        const pageToFetch = trimmed === "" ? currentPage : 1
        if (trimmed !== "" && currentPage !== 1) {
            setCurrentPage(1)
        }
        setSearchbox(trimmed)
        fetchData(pageToFetch, pageSize, sortColumn, sortOrder, trimmed)
    }

    const handleSortChange = (accessorKey) => {
        let newSortOrder = "asc"
        if (sortColumn === accessorKey) {
            newSortOrder = sortOrder === "asc" ? "desc" : "asc"
        }
        setSortColumn(accessorKey)
        setSortOrder(newSortOrder)
        fetchData(currentPage, pageSize, accessorKey, newSortOrder, searchbox)
    }

    const handleSubmit = async () => {
        if (!formData.name || !formData.subject || !formData.content) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            if (editingTemplate) {
                await emailTemplateService.updateTemplate(editingTemplate._id, formData)
                toast.success("Template updated successfully")
                handleCloseModal()
                fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
            } else {
                await emailTemplateService.createTemplate(formData)
                toast.success("Template created successfully")
                handleCloseModal()
                // Reset to page 1 when creating new template to see the newly added entry
                setCurrentPage(1)
                fetchData(1, pageSize, sortColumn, sortOrder, searchbox)
            }
        } catch (error) {
            toast.error(editingTemplate ? "Failed to update template" : "Failed to create template")
            console.error(error)
        }
    }

    const handleToggleActive = async (template) => {
        try {
            await emailTemplateService.updateTemplate(template._id, { isActive: !template.isActive })
            toast.success(`Template ${template.isActive ? "deactivated" : "activated"} successfully`)
            fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
        } catch (error) {
            toast.error("Failed to update template")
            console.error(error)
        }
    }

    const handleDeleteTemplates = async (ids) => {
        try {
            await emailTemplateService.deleteManyTemplates(ids)
            toast.success("Templates deleted successfully")
            setSelectedValues([])
            setDeleteMany(false)

            // If deleting all items on current page (except page 1), go back one page
            const remainingItems = data.length - ids.length
            if (remainingItems === 0 && currentPage > 1) {
                const newPage = currentPage - 1
                setCurrentPage(newPage)
                fetchData(newPage, pageSize, sortColumn, sortOrder, searchbox)
            } else {
                fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
            }
        } catch (error) {
            toast.error("Failed to delete templates")
            console.error(error)
        }
    }

    const handleSendEmail = (template) => {
        setSelectedTemplateForSend(template)
        setSelectedEmployees([])
        setSendOption('selective')
        // Fetch employees when modal opens to ensure fresh data
        if (employees.length === 0) {
            fetchEmployees()
        }
        onSendEmailOpen()
    }

    const handleSendEmailSubmit = async () => {
        if (!selectedTemplateForSend) return

        if (sendOption === 'selective' && (!selectedEmployees || selectedEmployees.length === 0)) {
            toast.error('Please select at least one employee')
            return
        }

        setSendingEmail(true)
        try {
            const payload = {
                templateId: selectedTemplateForSend._id,
                sendToAll: sendOption === 'all',
                employeeIds: sendOption === 'all' ? undefined : selectedEmployees.map(emp => emp.value)
            }

            const response = await emailTemplateService.sendEmailWithTemplate(selectedTemplateForSend._id, payload)
            toast.success(response.message || 'Emails sent successfully')
            onSendEmailClose()
            fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to send emails')
            console.error(error)
        } finally {
            setSendingEmail(false)
        }
    }

    const actionHeader = {
        Header: "Actions",
        isSortable: false,
        center: true,
        cell: ({ row }) => {
            const template = row.original.rawData;

            return (
                <Flex justify="center" align="center" w="100%">
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            size="sm"
                            variant="ghost"
                            fontWeight="bold"
                            fontSize="xl"
                            color="gray.600"
                            _hover={{ bg: "gray.100" }}
                        >
                            ⋮
                        </MenuButton>
                        <MenuList minW="150px" shadow="md">
                            {permission?.update && (
                                <>
                                    <MenuItem icon={<EmailIcon />} onClick={() => handleSendEmail(template)}>
                                        Send Email
                                    </MenuItem>
                                    <MenuItem icon={<img src={editIcon} alt="edit" style={{ width: '16px' }} />} onClick={() => handleOpenModal(template)}>
                                        Edit
                                    </MenuItem>
                                    <MenuItem
                                        icon={template.isActive ? <CloseIcon /> : <CheckIcon />}
                                        onClick={() => handleToggleActive(template)}
                                    >
                                        {template.isActive ? 'Deactivate' : 'Activate'}
                                    </MenuItem>
                                </>
                            )}
                            {permission?.delete && (
                                <MenuItem
                                    color="red.500"
                                    icon={<img src={deleteIcon} alt="delete" style={{ width: '16px' }} />}
                                    onClick={() => {
                                        setDeleteMany(true);
                                        setSelectedValues([row.original.id]);
                                    }}
                                >
                                    Delete
                                </MenuItem>
                            )}
                        </MenuList>
                    </Menu>
                </Flex>
            );
        }
    };

    const tableColumns = [
        {
            Header: "#",
            accessor: "_id",
            isSortable: false,
            width: 50
        },
        {
            Header: "Name",
            accessor: "name",
            cell: ({ row }) => (
                <Flex align="center" gap={2}>
                    <Text>{row.original.name}</Text>
                    {row.original.isDefault && (
                        <Badge colorScheme="green" fontSize="xs">Default</Badge>
                    )}
                </Flex>
            )
        },
        {
            Header: "Category",
            accessor: "category",
            cell: ({ value }) => {
                const colors = {
                    application: 'blue',
                    performance: 'purple',
                    document_request: 'orange',
                    bg_verification: 'teal',
                    reports: 'cyan',
                    loi: 'pink',
                    birthday_wishes: 'yellow',
                    attendance: 'green',
                    announcement: 'red',
                    notification: 'blue',
                    reminder: 'orange',
                    welcome: 'green',
                    custom: 'gray'
                };
                return (
                    <Badge colorScheme={colors[value] || 'gray'}>
                        {emailTemplateCategoryOptions.find(opt => opt.value === value)?.label || value}
                    </Badge>
                );
            }
        },
        {
            Header: "Status",
            accessor: "isActive",
            cell: ({ value }) => (
                <CustomStatus
                    statusText={value ? "Active" : "Inactive"}
                    color={value ? "#166534" : undefined}
                />
            )
        },
        {
            Header: "Usage Count",
            accessor: "usageCount"
        },
        {
            Header: "Last Used",
            accessor: "lastUsed"
        },
        {
            Header: "Created By",
            accessor: "createdBy"
        },
        ...(permission?.update || permission?.delete ? [actionHeader] : [])
    ];

    const [columns] = useState([...tableColumns])
    const [selectedColumns, setSelectedColumns] = useState([...tableColumns])

    const dataColumn = tableColumns.filter((item) => selectedColumns.some((column) => column.Header === item.Header))

    return (
        <div>
            <CommonCheckTable
                title={title}
                showInlineTitle={true}
                isLoading={isLoading}
                columnData={columns}
                dataColumn={dataColumn}
                allData={data}
                tableData={data}
                searchDisplay={displaySearchData}
                setSearchDisplay={setDisplaySearchData}
                searchedDataOut={searchedData}
                setSearchedDataOut={setSearchedData}
                tableCustomFields={[]}
                access={permission}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                setDelete={setDeleteMany}
                onOpen={() => handleOpenModal()}
                setCurrentPage={setCurrentPage}
                fetchData={fetchData}
                currentPage={currentPage}
                sortOrder={sortOrder}
                searchbox={searchbox}
                handleSortChange={handleSortChange}
                handleSearchChange={handleSearchChange}
                setSearchbox={setSearchbox}
                finalSearchedData={finalSearchedData}
                metadata={metadata}
                setFinalSearchedData={setFinalSearchedData}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalPages={metadata?.totalPages || 1}
                totalRecords={metadata?.totalRecords || 0}
                isPagination={true}
                AdvanceSearch={
                    <Button
                        variant="outline"
                        className="advance-search-button bg-transparent"
                        colorScheme="brand"
                        leftIcon={<SearchIcon />}
                        size="sm"
                    >
                        Advanced Search
                    </Button>
                }
            />

            {/* Create/Edit Template Modal */}
            <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
                <ModalOverlay />
                <ModalContent maxW="1000px" maxH="90vh">
                    <ModalHeader>
                        {editingTemplate ? 'Edit Template' : 'Create New Template'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6} overflowY="auto">
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <FormLabel>
                                    Template Name <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter template name"
                                />
                            </Box>

                            <Box>
                                <FormLabel>Description</FormLabel>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Brief description of this template"
                                    rows={2}
                                />
                            </Box>

                            <Box>
                                <FormLabel>
                                    Category <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                >
                                    {emailTemplateCategoryOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Select>
                            </Box>

                            <Box>
                                <FormLabel>
                                    Email Subject <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Input
                                    value={formData.subject}
                                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                    placeholder="Email subject line"
                                />
                            </Box>

                            <Box>
                                <FormLabel>
                                    Template Content <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <TextEditor
                                    toolbarId="email-template-content"
                                    value={formData.content || ''}
                                    handleChange={(value) => {
                                        const cleanedValue = value === "<p><br></p>" ? "" : value;
                                        setFormData({ ...formData, content: cleanedValue });
                                    }}
                                />
                            </Box>

                            <HStack spacing={6}>
                                <Box>
                                    <FormLabel mb={0}>Active</FormLabel>
                                    <Switch
                                        isChecked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                </Box>

                                <Box>
                                    <FormLabel mb={0}>Set as Default</FormLabel>
                                    <Switch
                                        isChecked={formData.isDefault}
                                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                                    />
                                </Box>
                            </HStack>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="outline" mr={3} onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleSubmit}>
                            {editingTemplate ? 'Update' : 'Create'} Template
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Send Email Modal */}
            <Modal isOpen={isSendEmailOpen} onClose={onSendEmailClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Send Email</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <Text fontWeight="semibold" mb={2}>
                                    Template: {selectedTemplateForSend?.name}
                                </Text>
                            </Box>

                            <Box>
                                <FormLabel mb={3}>Send Email To:</FormLabel>
                                <RadioGroup
                                    value={sendOption}
                                    onChange={(value) => {
                                        setSendOption(value)
                                        if (value === 'all') {
                                            setSelectedEmployees([])
                                        }
                                    }}
                                >
                                    <VStack align="start" spacing={3}>
                                        <Radio value="all">
                                            <Text ml={2}>All Employees</Text>
                                        </Radio>
                                        <Radio value="selective">
                                            <Text ml={2}>Selective Employees</Text>
                                        </Radio>
                                    </VStack>
                                </RadioGroup>
                            </Box>

                            {sendOption === 'selective' && (
                                <Box>
                                    <FormLabel>Select Employees</FormLabel>
                                    <ReactSelect
                                        isMulti
                                        options={employees}
                                        value={selectedEmployees}
                                        onChange={(selected) => setSelectedEmployees(selected || [])}
                                        placeholder={employees.length === 0 ? "Loading employees..." : "Select employees..."}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        isDisabled={employees.length === 0}
                                        noOptionsMessage={() => employees.length === 0 ? "Loading employees..." : "No employees found"}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                        }}
                                    />
                                    {employees.length === 0 && (
                                        <Text fontSize="xs" color="gray.500" mt={1}>
                                            No employees with email addresses found
                                        </Text>
                                    )}
                                </Box>
                            )}

                            {selectedTemplateForSend && (
                                <Box p={3} bg="gray.50" borderRadius="md">
                                    <Text fontSize="sm" fontWeight="semibold" mb={1}>Subject:</Text>
                                    <Text fontSize="sm">{selectedTemplateForSend.subject}</Text>
                                </Box>
                            )}
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="outline" mr={3} onClick={onSendEmailClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={handleSendEmailSubmit}
                            isLoading={sendingEmail}
                            loadingText="Sending..."
                        >
                            Send Email
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <CommonDeleteModel
                isOpen={deleteMany}
                onClose={() => setDeleteMany(false)}
                type="Templates"
                handleDeleteData={handleDeleteTemplates}
                ids={selectedValues}
                onCancel={() => setSelectedValues([])}
            />
        </div>
    );
};

export default EmailTemplates;
